import { OWUtils } from '../ow-utils';
import { OWGameWindowAnchor } from './ow-game-window-anchor';
import { OWWindowAnchorTypes } from './ow-window-anchor-types';
import Dictionary = overwolf.Dictionary;

type GetWindowStateResult = overwolf.windows.GetWindowStateResult;

// -----------------------------------------------------------------------------
export class OWWindow {
  private _name: string | null;
  private _id: string | null;

  // ---------------------------------------------------------------------------
  constructor(name: string | null = null) {
    this._name = name;
    this._id = null;
  }

  // ---------------------------------------------------------------------------
  public obtain(): Promise<overwolf.windows.WindowInfo | null> {
    return new Promise((resolve, reject) => {
      const cb = (res) => {
        if (res && res.status === 'success' && res.window && res.window.id) {
          this._id = res.window.id;

          if (!this._name) {
            this._name = res.window.name;
          }

          resolve(res.window);
        } else {
          this._id = null;
          reject(res.error);
        }
      };

      if (!this._name) {
        overwolf.windows.getCurrentWindow(cb);
      } else {
        overwolf.windows.obtainDeclaredWindow(this._name, cb);
      }
    });
  }

  // ---------------------------------------------------------------------------
  public async createGameWindowAnchor(
    anchorType: OWWindowAnchorTypes,
    margin?: number,
  ): Promise<OWGameWindowAnchor | null> {
    const winInfo = await this.obtain();
    if (!winInfo) {
      return null;
    }

    return new OWGameWindowAnchor(
      this,
      {
        width: winInfo.width,
        height: winInfo.height,
      },
      anchorType,
      margin,
    );
  }

  // ---------------------------------------------------------------------------
  public async center(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const win = await this.obtain();
        const scale = await OWUtils.getDPIScale();

        const left = Math.round(
          (window.screen.availWidth - win.width * scale) / 2,
        );
        const top = Math.round(
          (window.screen.availHeight - win.height * scale) / 2,
        );

        this.changePosition(Math.max(left, 0), Math.max(top, 0));
        resolve();
      } catch (e) {
        return reject(e);
      }
    });
  }

   // ---------------------------------------------------------------------------
   public async centerInMonitor(monitor: overwolf.utils.Display): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const win = await this.obtain();
        const scale = await OWUtils.getDPIScale();

        const left = monitor.x + Math.round(
          (monitor.width - win.width * scale) / 2
        );
    
        const top = monitor.y + Math.round(
          (monitor.height - win.height * scale) / 2
        );

        this.changePosition(left, top);
        resolve();
      } catch (e) {
        return reject(e);
      }
    });
  }

  // ---------------------------------------------------------------------------
  /**
   * Position the window to the bottom-right corner of the screen
   */
  public async bottomRight(): Promise<void> {
    return new Promise(async (resolve) => {
      let win: overwolf.windows.WindowInfo | null = null;

      const [wintemp, viewport] = await Promise.all([
        this.obtain(),
        OWUtils.getViewportSize(),
      ]);

      win = wintemp as overwolf.windows.WindowInfo;
      if (!win) {
        return resolve();
      }

      const scale: number = await OWUtils.getDPIScale();

      let left = viewport.width - win.width * scale;
      let top = viewport.height - win.height * scale;

      left = Math.max(left, 0);
      top = Math.max(top, 0);

      left = Math.floor(left);
      top = Math.floor(top);

      await this.changePosition(left, top);
      return resolve();
    });
  }

  // ---------------------------------------------------------------------------
  public async restore(): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.restore(id, (result) => {
        if (!result.success) {
          // eslint-disable-next-line max-len
          console.error(
            `[restore] - an error occurred, windowId=${id}, reason=${result.error}`
          );
          reject(result.error);
        }

        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async bringToFront(): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.bringToFront(id, (result) => {
        if (!result.success) {
          // eslint-disable-next-line max-len
          console.error(
            `[restore] - an error occurred, windowId=${id}, reason=${result.error}`
          );
          reject(result.error);
        }

        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async minimize(): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.minimize(id, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async maximize(): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.maximize(id, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async hide(): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.hide(id, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async changePosition(left: number, top: number): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.changePosition(id, left, top, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async changeSize(width: number, height: number): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.changeSize(id, width, height, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async setMinSize(minWidth: number, minHeight: number): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.setMinSize(id, minWidth, minHeight, (result) => {
        const { success } = result;
        if (success) {
          resolve();
        } else {
          reject(result.error);
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async dragMove() {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.dragMove(id, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve(result);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async dragResizeLeft() {
    await this.assureObtained();

    return new Promise(resolve => {
      const id: string = this._id as string;
      overwolf.windows.dragResize(
        id,
        overwolf.windows.enums.WindowDragEdge.Left
      );
      return resolve(null);
    });
  }

  // ---------------------------------------------------------------------------
  public async dragResizeRight() {
    await this.assureObtained();

    return new Promise(resolve => {
      const id: string = this._id as string;
      overwolf.windows.dragResize(
        id,
        overwolf.windows.enums.WindowDragEdge.Right
      );
      return resolve(null);
    });
  }

  // ---------------------------------------------------------------------------
  public async getWindowState(): Promise<GetWindowStateResult> {
    return new Promise((resolve, reject) => {
      const id = this._id || this._name;
      overwolf.windows.getWindowState(id, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve(result);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async setTopmost(): Promise<boolean> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;
      overwolf.windows.setTopmost(id, true, (result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve(result.success);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static async getCurrentInfo(): Promise<overwolf.windows.WindowInfo> {
    return new Promise((resolve, reject) => {
      overwolf.windows.getCurrentWindow((result) => {
        if (!result.success) {
          reject(result.error);
        }

        resolve(result.window);
      });
    });
  }

  // ---------------------------------------------------------------------------
  public static async getOpenWindows(): Promise<Dictionary<Window>> {
    return new Promise(async resolve => {
      overwolf.windows.getOpenWindows((windows) => {
        resolve(windows);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // NOTE(twolf): This API is a bit 'broken' - it only works for the current
  // window which is calling this API (as opposed to accepting a windowId param)
  // This is why this function is static.
  public static async setMute(mute = true): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!overwolf.windows.setMute) {
        return reject('setMute does not exist');
      }

      overwolf.windows.setMute(mute, (result) => {
        if (!result.success) {
          reject(result.error);
        } else {
          resolve();
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  public async close(): Promise<void> {
    const result = await this.getWindowState();
    if (result.success && result.window_state === 'closed') {
      return;
    }

    await this.internalClose();
  }

  // ---------------------------------------------------------------------------
  // Useful for debugging - refresh the window's html content
  public async reloadPage(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const win = await this.obtain();

        const windows = await OWWindow.getOpenWindows();
        if (!windows[win.name]) {
          return;
        }

        windows[win.name].location.reload();
        resolve();
      } catch (e) {
        return reject(e);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Private Function
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  private async assureObtained(): Promise<void> {
    await this.obtain();
  };

  // ---------------------------------------------------------------------------
  private async internalClose(): Promise<void> {
    await this.assureObtained();

    return new Promise((resolve, reject) => {
      const id: string = this._id as string;

      overwolf.windows.close(id, (res) => {
        if (res && res.success) {
          resolve();
        } else {
          reject(res);
        }
      });
    });
  }
}
